import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Dialog, Paper, useMediaQuery } from "@mui/material";
import { DeviceHub, Security } from "@mui/icons-material";
import Button from "../../base/Button";
import InfoBoxGroup from "../../base/InfoBoxGroup";
import InfoBox from "../../base/InfoBox";
import ConnectionsTable from "./ConnectionsTable";
import Modal from "../../base/Modal";
import { useDispatch, useSelector } from "../../../store";
import {
  clearConnection,
  createConnection,
  deleteConnection,
  editConnection,
  fetchConnection,
  fetchConnections,
} from "../../../slices/connection";
import AddEditConnection, {
  ConnectionFormSubmitHandler,
} from "./AddEditConnection";
import Connection from "../../../types/connection";
import ConnectionsStack from "./ConnectionsStack";
import DeleteConfirmation from "../../base/DeleteConfirmation";
import SettingsForm, { SettingsFormSubmitHandler } from "./SettingsForm";
import { fetchSettings, saveSettings } from "../../../slices/settings";
import Settings from "../../../types/settings";
import checkRole, { UserRoles } from "../../../helpers/checkRole";
import theme from "../../../theme";

interface Props {}

const ConnectionsView: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const { status } = useSelector((store) => store.connection);
  const { loggedInUser, userCompany } = useSelector((store) => store.auth);

  const connectionsFormSubmitHandlerRef =
    useRef<ConnectionFormSubmitHandler | null>(null);
  const settingsFormSubmitHandlerRef = useRef<SettingsFormSubmitHandler | null>(
    null
  );

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  const [modalOpen, setModalOpen] = useState<
    "add" | "edit" | "delete" | "none"
  >("none");
  const [selectedConnection, setSelectedConnection] = useState<
    number | undefined
  >();

  const handleSaveSettings = async (data: Settings) => {
    await dispatch(saveSettings(data));
  };

  const handleAddNewConnection = () => {
    dispatch(clearConnection());
    setModalOpen("add");
  };

  const handleEditConnection = (id: number) => {
    dispatch(fetchConnection(id));
    setModalOpen("edit");
  };

  const handleOpenDelete = (id: number) => {
    setSelectedConnection(id);
    setModalOpen("delete");
  };

  const handleClose = () => {
    setModalOpen("none");
    dispatch(clearConnection());
    dispatch(fetchConnections());
    setSelectedConnection(undefined);
  };

  const handleSaveConnection = async (data: Connection) => {
    if (modalOpen === "add") {
      await dispatch(createConnection(data));
    } else if (modalOpen === "edit") {
      await dispatch(editConnection(data));
    }

    handleClose();
  };

  const handleDelete = async () => {
    await dispatch(deleteConnection(selectedConnection!));
    handleClose();
  };

  const infoBoxes = [
    {
      text: `Select the system types and systems below to enable for ${
        userCompany?.companyName ?? "your organization"
      }.`,
      icon: (
        <Box
          component="img"
          height="70px"
          width="100%"
          sx={{ objectFit: "cover" }}
          src={userCompany?.logo || "/static/images/SVPro.png"}
          alt={userCompany?.companyName}
        />
      ),
    },
    {
      text: "Add all the connections to pull report data from, then add one or more connections to tiles to aggregate data from each.",
      icon: <DeviceHub fontSize="inherit" color="secondary" />,
    },
    checkRole(loggedInUser!, UserRoles.GlobalAdmin)
      ? {
          text: `Click here to visit the Admin Portal to update other settings.`,
          icon: <Security fontSize="inherit" color="warning" />,
          link: `${process.env.REACT_APP_AUTH_URL}/app/settings`,
        }
      : {
          text: "Global settings can be updated in the Admin Portal.",
          icon: <Security fontSize="inherit" color="warning" />,
        },
  ];

  const xs = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Container sx={{ marginBottom: 8 }}>
      <InfoBoxGroup
        header={"Connection Settings"}
        content={infoBoxes.map((info, index) => {
          return (
            <InfoBox
              key={index}
              text={info.text}
              icon={info.icon}
              link={info.link}
            />
          );
        })}
      />
      <Paper
        sx={{
          paddingTop: xs ? 1 : 3,
          paddingBottom: xs ? 2 : 3,
          paddingX: { xs: 2, md: 5 },
          marginBottom: 5,
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          marginBottom={2}
          sx={{ maxWidth: "100%" }}
        >
          <SettingsForm
            onSave={handleSaveSettings}
            ref={settingsFormSubmitHandlerRef}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          width="100%"
          justifyContent="flex-end"
          gap={2}
        >
          <Box display="flex" flexDirection="column" justifyContent="center">
            <Button
              label="Save"
              variant={"contained"}
              color={"primary"}
              onClick={() => settingsFormSubmitHandlerRef.current?.()}
              disabled={status === "loading"}
            />
          </Box>
        </Box>
      </Paper>
      <Paper
        sx={{
          paddingTop: { xs: 2, md: 3 },
          paddingBottom: { xs: 1, md: 3 },
          paddingX: { xs: 2, md: 5 },
          marginBottom: 5,
        }}
      >
        <Box display="flex" flexDirection="row" justifyContent="flex-end">
          <Button
            label="Add New"
            variant="contained"
            color="info"
            onClick={handleAddNewConnection}
          />
        </Box>
        <Box
          display={{ xs: "none", md: "flex" }}
          flexDirection="row"
          justifyContent="center"
          marginY={2}
          sx={{ maxWidth: "100%" }}
        >
          <ConnectionsTable
            handleEdit={handleEditConnection}
            handleDelete={handleOpenDelete}
          />
        </Box>
        <Box
          display={{ xs: "flex", md: "none" }}
          flexDirection="row"
          justifyContent="center"
          marginY={2}
          sx={{ width: "100%" }}
        >
          <ConnectionsStack
            handleEdit={handleEditConnection}
            handleDelete={handleOpenDelete}
          />
        </Box>
      </Paper>
      <Modal
        open={modalOpen === "add" || modalOpen === "edit"}
        onClose={handleClose}
        onSave={() => connectionsFormSubmitHandlerRef.current?.()}
        title={`${modalOpen === "add" ? "Add New" : "Edit"} Connection`}
        loading={status === "loading"}
      >
        <AddEditConnection
          type={modalOpen === "add" ? "add" : "edit"}
          onSubmit={handleSaveConnection}
          ref={connectionsFormSubmitHandlerRef}
        />
      </Modal>
      <Dialog open={modalOpen === "delete"}>
        <DeleteConfirmation
          name="this connection"
          extraInfo="This connection will be removed from all tiles."
          handleClose={handleClose}
          handleDelete={handleDelete}
          loading={status === "loading"}
        />
      </Dialog>
    </Container>
  );
};

export default ConnectionsView;
